/* eslint-disable */
import React from "react";
import { CalendarMonth } from '../../../controls/CalendarMonth';
import { CalendarHierarchy, InputCalendarHierarchyItem } from '../../../controls/CalendarHierarchy';
import { ICalendarItem, useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { equal } from "../../../../../shared";

export const CalendarHierarchyControl: React.FC = () => {
    const [controlItems, setControlItems] = React.useState<InputCalendarHierarchyItem[] | undefined>();
    const [dictItems] = useDictionary(dictionaryService, "calendar-origin");
    const [controlValue, setControlValue] = React.useState<number[] | undefined>();
    const [calendarValue, setCalendarValue] = useParameter<number[]>("@calendar");
    const [calendarDisplayValue, setCalendarDisplayValue] = useParameter<number[]>("@calendar_display");
    const [yearFrequency, monthFrequency, weekFrequency] = [1, 4, 7];

    React.useEffect(function updateStateValueByParameter() {
        if (calendarDisplayValue && !equal(calendarDisplayValue, controlValue)) {
            setControlValue(calendarDisplayValue);
        }
    }, [calendarDisplayValue]);

    React.useEffect(function updateControlsItemsByDictionaryItems() {
        const candiatesControlItems = dictItems?.map<InputCalendarHierarchyItem & { frequency: number }>(item => ({
            id: Number(item.id),
            parent: item.getValue<number>('parent'),
            name: item.getValue<string>('name') || '',
            frequency: item.getValue<number>('frequencyId')!,
        }));

        const [minYear, maxYear] = [120000101, Number(`1${new Date().getFullYear()}0101`)];
        const nextControlItems = candiatesControlItems?.reduce((result, item) => {
            if (item.frequency !== weekFrequency) {
                const itemId = item.id as number;
                if (item.frequency === yearFrequency && itemId >= minYear && itemId <= maxYear) {
                    result.push(item);
                } else if (item.frequency === monthFrequency) {
                    const paretQuartal = dictItems?.find(dictItem => equal(+dictItem.id, item.parent));
                    const parentYearId = paretQuartal?.getValue<number>('parent')!;
                    if (parentYearId >= minYear && parentYearId <= maxYear) {
                        item.parent = parentYearId;
                        result.push(item);
                    }
                }
            }
            return result;
        }, [] as typeof candiatesControlItems);

        setControlItems(nextControlItems);
    }, [JSON.stringify(dictItems)]);


    const yearsFirstWeeks = React.useMemo(() => {
        const yearsItems = dictItems?.filter(item => yearFrequency === item.getValue<number>("frequencyId"));
        const monthesIds = yearsItems?.map(yearParent => Number(monthFrequency.toString() + yearParent.id.slice(1)));
        const weeks = monthesIds?.map(monthId => {
            const firstWeek = dictItems?.find(dictItem => {
                const isWeek = weekFrequency === dictItem.getValue<number>("frequencyId");
                const dictItemMonthId = dictItem.getValue<number>("parent")!;
                return isWeek && dictItemMonthId === monthId;
            });
            return firstWeek;
        });
        return new Map(weeks?.filter(Boolean).map(week => {
            const yearId = Number(yearFrequency.toString() + week!.id.slice(1, 5) + '0101');
            return [yearId, week];
        }));
    }, [dictItems]);

    const handlerChange = React.useCallback((value: unknown) => {
        const monthesOrYearsIds = value as number[];
        setCalendarDisplayValue(monthesOrYearsIds);

        const weeksIds = monthesOrYearsIds.flatMap(monthOrYearId => {
            const firstCharacter = monthOrYearId.toString().slice(0, 1);
            const frequency = Number(firstCharacter);
            const isMonth = frequency === monthFrequency;
            if (isMonth) {
                const monthWeeks = dictItems?.filter(candidateWeekItem => {
                    const isWeek = weekFrequency === candidateWeekItem.getValue<number>("frequencyId");
                    if (isWeek) {
                        const weekParentId = candidateWeekItem.getValue<number>("parent");
                        return weekParentId === monthOrYearId;
                    }
                    return false;
                });
                return monthWeeks?.map(week => Number(week.id)) || [];
            }
            const firstMonthKey = `${String(monthOrYearId).slice(1,5)}01`;
            const firstMonthWeeks = dictItems?.filter(candidateWeekItem => {
                const isFirstMonth = String(candidateWeekItem.id).slice(1,7) === firstMonthKey;
                return isFirstMonth;
            });
            return firstMonthWeeks?.map(week => Number(week.id)) || [];
        });

        if (weeksIds) {
            setCalendarValue(weeksIds);
        }
    }, [dictItems, yearsFirstWeeks]);

    return <CalendarHierarchy selectableParent label="Calendar" multiple value={controlValue} onChange={handlerChange} items={controlItems} />;
};
